/**
 * Register service worker
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker is not supported in this browser.');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log('Service Worker registered:', registration.scope);
  } catch (error) {
    console.error('Service Worker registration failed:', error);
  }
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Notifications are not supported in this browser.');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Subscribe to Web Push notifications
 * Returns the subscription object, or null on failure
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push notifications are not supported.');
    return null;
  }

  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') return null;

    const registration = await navigator.serviceWorker.ready;

    // Convert VAPID key from base64url to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
    });

    return subscription;
  } catch (e) {
    console.error('Push subscription failed:', e);
    return null;
  }
}

/**
 * Unsubscribe from Web Push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
    return true;
  } catch (e) {
    console.error('Push unsubscribe failed:', e);
    return false;
  }
}

/**
 * Get current push subscription (if any)
 */
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Show local notification (for meal/leave/freshday alerts)
 */
export function showLocalNotification(title: string, body: string): void {
  if (!('Notification' in window)) return;

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted.');
    return;
  }

  // Try to use service worker notification first (works in background)
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: `local-${Date.now()}`,
      });
    });
  } else {
    // Fallback to regular notification
    new Notification(title, {
      body,
      icon: '/icon-192.png',
    });
  }
}

/** 프레시데이 진행 요일 — 화(2)·목(4). 요일 필터로만 쓰이며 사용자가 변경할 수 없다 */
const FRESHDAY_DAYS = [2, 4];

/**
 * Setup meal/leave/freshday alert timers based on user settings
 * Returns a cleanup function to clear the timers
 */
export function setupAlertTimers(settings: {
  mealAlertEnabled: boolean;
  mealAlertTime: string;  // "HH:mm"
  leaveAlertEnabled: boolean;
  leaveAlertTime: string; // "HH:mm"
  freshdayAlertEnabled: boolean;
  freshdayAlertTime: string; // "HH:mm"
}): () => void {
  const timers: ReturnType<typeof setTimeout>[] = [];

  const now = new Date();

  if (settings.mealAlertEnabled && settings.mealAlertTime) {
    const ms = getMsUntilTime(now, settings.mealAlertTime);
    if (ms !== null) {
      const timer = setTimeout(() => {
        showLocalNotification('식사 알림', '식사 시간이 되었습니다!');
      }, ms);
      timers.push(timer);
    }
  }

  if (settings.leaveAlertEnabled && settings.leaveAlertTime) {
    const ms = getMsUntilTime(now, settings.leaveAlertTime);
    if (ms !== null) {
      const timer = setTimeout(() => {
        showLocalNotification('퇴근 알림', '퇴근 시간이 되었습니다!');
      }, ms);
      timers.push(timer);
    }
  }

  // 프레시데이는 화·목요일에만 진행되므로 해당 요일에만 예약한다
  if (
    settings.freshdayAlertEnabled &&
    settings.freshdayAlertTime &&
    FRESHDAY_DAYS.includes(now.getDay())
  ) {
    const ms = getMsUntilTime(now, settings.freshdayAlertTime);
    if (ms !== null) {
      const timer = setTimeout(() => {
        showLocalNotification('프레시데이 알림', '오늘은 프레시데이입니다!');
      }, ms);
      timers.push(timer);
    }
  }

  // Return cleanup function
  return () => {
    timers.forEach((timer) => clearTimeout(timer));
  };
}

/**
 * Calculate milliseconds until the target time today.
 * Returns null if the time has already passed today.
 */
function getMsUntilTime(now: Date, timeStr: string): number | null {
  const [hoursStr, minutesStr] = timeStr.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  if (isNaN(hours) || isNaN(minutes)) return null;

  const target = new Date(now);
  target.setHours(hours, minutes, 0, 0);

  const diff = target.getTime() - now.getTime();

  // If time has already passed today, skip
  if (diff <= 0) return null;

  return diff;
}
